import { useTranslation } from 'react-i18next';
import { useAuth } from '@/hooks/useAuth';
import { CounterControl } from '@/features/apt-counter/CounterControl';
import { DoctorSwitch } from '@/features/apt-counter/DoctorSwitch';
import { TodayQueueCard } from '@/features/apt-counter/TodayQueueCard';
import { QrCard } from '@/components/synapse/QrCard';
import { useAptCounter } from '@/features/apt-counter/useAptCounter';
import { tenantHref } from '@/routing/tenant';

export function AptCounterPage() {
  const { t } = useTranslation();
  const { activeClinic } = useAuth();
  const { data, isLoading, error } = useAptCounter();

  if (!activeClinic) {
    return (
      <div className="mx-auto max-w-2xl">
        <p className="mt-2 text-[13.5px] text-muted">{t('aptCounter.joinClinicFirst')}</p>
      </div>
    );
  }

  if (isLoading || !data) {
    return (
      <div className="mx-auto max-w-2xl">
        <h1 className="text-[22px] font-semibold tracking-[-0.02em] text-ink">{t('aptCounter.title')}</h1>
        <p className="mt-2 text-[13.5px] text-muted">
          {error ? t('aptCounter.couldNotLoad') : t('common.loading')}
        </p>
      </div>
    );
  }

  // Addressed by slug, not id: this URL is printed and stuck on a wall, so
  // `…/acme/p/counter` beats a 36-character uuid. Posters printed before slugs
  // existed still work — `LegacyPublicRedirect` forwards them.
  const publicUrl = `${window.location.origin}${tenantHref(activeClinic.slug, '/p/counter')}`;
  const fileBase = `apt-counter-${activeClinic.slug}`;

  return (
    <div className="mx-auto max-w-5xl space-y-6">
      <header>
        <h1 className="text-[22px] font-semibold tracking-[-0.02em] text-ink">{t('aptCounter.title')}</h1>
        <p className="mt-1 text-[13.5px] text-muted">{t('aptCounter.subtitle')}</p>
      </header>

      <DoctorSwitch doctorIn={data.doctorIn} doctorName={data.doctorName} />

      <div className="grid gap-6 lg:grid-cols-[1fr_auto]">
        <div className="rounded-lg border border-line bg-paper">
          <CounterControl value={data.currentNumber} />
        </div>
        <div className="lg:w-[320px]">
          <QrCard
            url={publicUrl}
            fileBase={fileBase}
            title={t('aptCounter.qrTitle')}
            ariaLabel={t('aptCounter.qrAriaLabel')}
          />
        </div>
      </div>

      <TodayQueueCard currentNumber={data.currentNumber} />
    </div>
  );
}
